import { initSentry, Sentry } from "@/lib/sentry"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { ThemeProvider } from "@/components/theme-provider.tsx"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { routeTree } from "./routeTree.gen"
import { TooltipProvider } from "@/components/ui/tooltip"
import { ErrorFallback } from "@/components/error-fallback"
import { ApiError } from "@/lib/api"

import "./index.css"

const throwOnApiNotFound = (error: Error) =>
  error instanceof ApiError && error.status === 404

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { throwOnError: throwOnApiNotFound },
    mutations: { throwOnError: throwOnApiNotFound },
  },
})
const router = createRouter({ routeTree, context: { userFlags: undefined } })

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

initSentry(router)

createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <RouterProvider router={router} />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
)
